import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  type DocumentSnapshot,
  type FirestoreDataConverter,
  getDoc,
  getDocs,
  onSnapshot,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  setDoc,
  type SnapshotOptions,
  type Unsubscribe,
  type WithFieldValue,
} from 'firebase/firestore';
import { getCurrentUser } from './authClient';
import { getFirestoreInstance } from './firestoreClient';

export const firestoreAddressConverter: FirestoreDataConverter<Pagcomanda.Address> = {
  toFirestore(address: WithFieldValue<Pagcomanda.Address>) {
    return address;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.Address {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Address document data is empty');
    }

    return data as Pagcomanda.Address;
  },
};

export const addressConverter = firestoreAddressConverter;

export const firestoreUserConverter: FirestoreDataConverter<Pagcomanda.User> = {
  toFirestore(user: WithFieldValue<Pagcomanda.User>) {
    return user;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.User {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('User document data is empty');
    }

    return data as Pagcomanda.User;
  },
};

export const userProfileConverter = firestoreUserConverter;

export const firestoreCardConverter: FirestoreDataConverter<Pagcomanda.Card> = {
  toFirestore(card: WithFieldValue<Pagcomanda.Card>) {
    return card;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.Card {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Card document data is empty');
    }

    return data as Pagcomanda.Card;
  },
};

export const cardConverter = firestoreCardConverter;

function userCardsCollection(uid: string) {
  return collection(
    getFirestoreInstance(),
    'users',
    uid,
    'cards',
  ).withConverter(firestoreCardConverter);
}

export async function getUserAddresses(
  uid?: string,
): Promise<Array<{ id: string; data: Pagcomanda.Address }>> {
  const targetUid = uid ?? getCurrentUser()?.uid;
  if (!targetUid) return [];

  const db = getFirestoreInstance();
  const addressesRef = collection(db, 'users', targetUid, 'addresses').withConverter(
    firestoreAddressConverter,
  );
  const snapshot = await getDocs(addressesRef);

  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    data: docSnap.data(),
  }));
}

export async function addUserAddress(
  address: Pagcomanda.Address,
  uid?: string,
): Promise<string | null> {
  const targetUid = uid ?? getCurrentUser()?.uid;
  if (!targetUid) return null;

  const db = getFirestoreInstance();
  const addressesRef = collection(db, 'users', targetUid, 'addresses').withConverter(
    firestoreAddressConverter,
  );
  const docRef = await addDoc(addressesRef, address);
  return docRef.id;
}

export async function deleteUserAddress(
  addressId: string,
  uid?: string,
): Promise<boolean> {
  const targetUid = uid ?? getCurrentUser()?.uid;
  if (!targetUid) return false;

  const db = getFirestoreInstance();
  const addressRef = doc(db, 'users', targetUid, 'addresses', addressId);
  await deleteDoc(addressRef);
  return true;
}

export async function getUserProfile(
  uid?: string,
): Promise<Pagcomanda.User | null> {
  const targetUid = uid ?? getCurrentUser()?.uid;
  if (!targetUid) return null;

  const db = getFirestoreInstance();
  const userRef = doc(db, 'users', targetUid).withConverter(firestoreUserConverter);
  const snapshot = await getDoc(userRef);

  return snapshot.exists() ? snapshot.data() : null;
}

export async function saveUserProfile(
  profileData: Partial<Pagcomanda.User> | Record<string, unknown>,
  uid?: string,
): Promise<void> {
  const targetUid = uid ?? getCurrentUser()?.uid;
  if (!targetUid) return;

  const db = getFirestoreInstance();
  const userRef = doc(db, 'users', targetUid);
  await setDoc(userRef, profileData, { merge: true });
}

export function observeUserProfile(
  uid: string,
  onNext: (snapshot: DocumentSnapshot<Pagcomanda.User>) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const userRef = doc(
    getFirestoreInstance(),
    'users',
    uid,
  ).withConverter(firestoreUserConverter);

  return onError ? onSnapshot(userRef, onNext, onError) : onSnapshot(userRef, onNext);
}

export function observeUserCards(
  uid: string,
  onNext: (snapshot: QuerySnapshot<Pagcomanda.Card>) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onError
    ? onSnapshot(userCardsCollection(uid), onNext, onError)
    : onSnapshot(userCardsCollection(uid), onNext);
}

export async function deleteUserCard(
  cardId: string,
  uid?: string,
): Promise<void> {
  const targetUid = uid ?? getCurrentUser()?.uid;
  if (!targetUid) return;

  const cardRef = doc(getFirestoreInstance(), 'users', targetUid, 'cards', cardId);
  await deleteDoc(cardRef);
}
